import ConfigAPI from '@/api/ConfigAPI'
import definitionSerializer from '@/appComponents/oldWorkbench/targetDefinition/services/serializer'
import transformAttributes from '@/components/common/attributes'
import WorkbenchWCanvasSlotActionsGenerator, {
  SET_TARGET_DEFINITION_DIRTY,
  SET_DATA_FROM_TARGET,
  UPDATE_STREAMING_OPTION,
  FETCH_DATA_FROM_TARGET_FOR_GRAPHS,
  SET_TARGET_ENVIRONMENT,
  SET_TARGET_DEFINITION,
} from '@/views/workbenchCanvas/store/WorkbenchWCanvasSlotActionsGenerator'

import { fetchSmTargetData, fetchSingleTarget } from '@/api/index'

import { SET_TOP_TSP, SET_TOP_SOCIAL_AFFINITY, SET_REACH, SET_DATA_SOURCES, SET_AUDIENCE_SIZE } from './mutations'

import { SET_METRICS, SET_LOADING, SET_QUICK_ADD, SET_FETCHING_TARGET } from '@/views/workbenchCanvas/store/mutations'

import {
  SET_DEFINITION,
  SET_TO_VIEW_ONLY,
  REMOVE_VIEW_ONLY,
  INITIALIZE_TARGET_DEF,
} from '@/appComponents/oldWorkbench/targetDefinition/store/actions'
import {
  GET_REVISION_HISTORY,
  FORWARD_REVISION_HISTORY,
  SET_TARGET_BRANCH,
  SET_PRIMARY_BRANCH,
  SET_MISSING_BRANCH,
} from '@/appLogic/revisionHistory/store/actions'

import { SET_CURRENT_TARGET, SET_SELECTED_VIEWERSHIP_NETWORKS } from '@/appLogic/targets/currentTarget/store/actions'
import Bluebird from 'bluebird'
import generateLabel from '@/appLogic/targetDefinition/LabelMoveGenerator'
import { DATASOURCES } from '@/appLogic/targets/Constants'

const smConfigApi = ConfigAPI.getConfigFilesForConfigType('sm-config.json')

class WorkbenchActions extends WorkbenchWCanvasSlotActionsGenerator {
  initActionBody({ dispatch, commit, getters, state }, id) {
    commit(SET_LOADING, true)
    Bluebird.props({
      revisionHistory: dispatch(GET_REVISION_HISTORY, { targetId: id }),
      config: smConfigApi.fetchConfig(),
      // TODO need the entire full object here
      target: fetchSingleTarget({ target_id: id }).then(resp => resp.data),
    })
      .then(data => {
        const { config, target } = data
        commit(SET_CURRENT_TARGET, target)
        dispatch(SET_TARGET_BRANCH, { name: target.display_name })
        if (!getters.revisionHistoryCurrentBranch) {
          dispatch(SET_MISSING_BRANCH, { id: target.id, display_name: target.display_name })
        } else {
          dispatch(SET_PRIMARY_BRANCH, { name: target.display_name })
        }
        if (getters.isTargetActivated || getters.isTargetArchived) {
          dispatch(SET_TO_VIEW_ONLY)
        } else {
          dispatch(REMOVE_VIEW_ONLY)
        }
        return smConfigApi.fetchDatasets(config.datasets).then(datasets => {
          return { datasets, config }
        })
      })
      .then(({ datasets, config }) => {
        // datasets and datasources are used interchangeably
        // prune gigya datasources from available datasets (AM-1209)
        commit(
          SET_DATA_SOURCES,
          datasets
            .filter(d => !d.id.startsWith('gigya'))
            .map(dataset => Object.freeze(transformAttributes(dataset, dataset.id))),
        )
        commit(SET_QUICK_ADD, config.quick_add)
        commit(SET_DEFINITION, definitionSerializer.deserialize(getters.currentTarget.target_definition.query, getters))
        if (getters.currentTarget.target_definition.hasOwnProperty('viewSource')) {
          dispatch(UPDATE_STREAMING_OPTION, getters.currentTarget.target_definition.viewSource)
        }
        if (getters.isTargetActivated) {
          let currentTarget = getters.currentTarget
          dispatch(SET_DATA_FROM_TARGET, {
            metrics: currentTarget.metrics,
            audienceSize: currentTarget.audience_size,
            topTsp: currentTarget.tsp_attributes,
            topSocialAffinity: currentTarget.social_affinity,
            reach: currentTarget.brand_reach,
          })
          commit(SET_LOADING, false)
        } else {
          return dispatch(FETCH_DATA_FROM_TARGET_FOR_GRAPHS).then(() => {
            dispatch(INITIALIZE_TARGET_DEF, getters.getDefinition)
          })
        }
      })
      .then(() => dispatch(SET_TARGET_DEFINITION_DIRTY, false))
  }

  targetDataToSave({ state, getters }) {
    const rhpm = getters.revisionHistoryPrimaryMove
    if (rhpm) {
      // TODO Revision History should include the Networks
      const td = rhpm.target_data
      // TODO add in tsp_attributes
      // TODO add in social_affinity
      // TODO add in brand_reach
      return {
        id: getters.currentTarget.id,
        username: getters.currentTarget.username,
        display_name: getters.currentTarget.display_name,
        target: getters.currentTarget.target,
        target_definition: rhpm.target_definition,
        metrics: td.metrics,
        audience_size: td.audienceSize,
        tsp_attributes: td.topTsp,
        social_affinity: td.topSocialAffinity,
        brand_reach: state.canvas.reach,
      }
    } else {
      return {
        id: getters.currentTarget.id,
        username: getters.currentTarget.username,
        display_name: getters.currentTarget.display_name,
        target: getters.currentTarget.target,
        target_definition: getters.targetDefinition,
        metrics: state.metrics,
        audience_size: state.canvas.audienceSize,
        tsp_attributes: state.canvas.topTsp,
        social_affinity: state.canvas.topSocialAffinity,
        brand_reach: state.canvas.reach,
      }
    }
  }

  fetchDataFromTargetForGraphs({ dispatch, commit, getters }) {
    if (getters.isFetchingTarget) return
    commit(SET_LOADING, true)
    commit(SET_FETCHING_TARGET, true)
    const targetDefinitionToFetch = getters.targetDefinition
    return fetchSmTargetData(targetDefinitionToFetch).then(resp => {
      if (targetDefinitionToFetch !== getters.targetDefinition) {
        // target def has changed since the last query happened...fetch latest data
        commit(SET_FETCHING_TARGET, false)
        dispatch(FETCH_DATA_FROM_TARGET_FOR_GRAPHS)
      } else {
        const targetData = {
          metrics: resp.metrics,
          audienceSize: resp.audienceSize && resp.audienceSize.audience,
          topTsp: resp.topTsp && resp.topTsp.top_10_tsp,
          topSocialAffinity: resp.topSocialAffinity && resp.topSocialAffinity.top_10_social_affinity,
          reach: resp.reach && resp.reach.brand_engagement_reach,
        }
        dispatch(SET_DATA_FROM_TARGET, targetData)
        const lastMove = getters.targetDefinitionLastAttributeCommand
        const userHasMadeAForwardAction = lastMove
        if (userHasMadeAForwardAction) {
          // TODO add the label based off the lastMove
          dispatch(FORWARD_REVISION_HISTORY, {
            targetData,
            targetDefinition: targetDefinitionToFetch,
            audience: targetData.audienceSize,
            label: generateLabel(lastMove),
          })
        }
        commit(SET_LOADING, false)
        commit(SET_FETCHING_TARGET, false)
      }
    })
  }

  dataFromTargetToSet(commit, data) {
    const { metrics, audienceSize, topTsp, topSocialAffinity, reach } = data
    commit(SET_METRICS, metrics)
    commit(SET_AUDIENCE_SIZE, audienceSize)
    commit(SET_TOP_TSP, topTsp)
    commit(SET_TOP_SOCIAL_AFFINITY, topSocialAffinity)
    commit(SET_REACH, reach)
  }

  additionalActions() {
    return {
      [SET_TARGET_ENVIRONMENT]: (context, targetData) => {
        context.dispatch(SET_FETCHING_TARGET, true)
        context.dispatch(SET_TARGET_DEFINITION, targetData.targetDefinition)
        context.dispatch(SET_DATA_FROM_TARGET, targetData.targetData)
        // we have to set a timeout because of the watcher
        setTimeout(() => {
          context.dispatch(SET_FETCHING_TARGET, false)
        }, 50)
      },
      [SET_TARGET_DEFINITION]: (context, targetDefinition) => {
        context.dispatch(SET_DEFINITION, definitionSerializer.deserialize(targetDefinition.query, context.getters))
        context.commit(SET_SELECTED_VIEWERSHIP_NETWORKS, targetDefinition.networks)
        context.dispatch(UPDATE_STREAMING_OPTION, targetDefinition.viewSource)
      },
    }
  }
}

export default WorkbenchActions



// WEBPACK FOOTER //
// ./src/app/views/workbench/SM/store/actions.js